// Python course demo: a turtle drawing a 36-point star on the deck.
//
// Step it by hand and you get one chord per click and a long evening ahead.
// Run the loop and three lines of Python draw the whole figure — the classic
// first "oh" of learning to program, in the language the course teaches.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { CornerDownLeft, Play } from 'lucide-react';
import { Fitted, HaloRing, PLATFORM_TOP, ShadowBlob, useCloned } from '../helpers';
import DemoShell from './DemoShell';

/* ── The figure ──
   A {36/17} star polygon: 36 chords, each turning 170°. Its circumradius is
   s / (2·sin(17π/36)), so the step length is derived from the radius we want
   rather than guessed — at 170° the chords run nearly across the whole circle,
   which is exactly why the figure is worth watching being drawn. */
export const STEPS = 36;
const TURN = (170 * Math.PI) / 180;
const RADIUS = 1.62;
const STEP_LENGTH = RADIUS * 2 * Math.sin((17 * Math.PI) / 36);
/** Milliseconds between iterations while the loop is running. */
const RUN_INTERVAL = 55;

const THICKNESS = 0.05;
/** Height of the drawing above the deck. */
const LIFT = 0.06;

/** Every chord's midpoint, heading and endpoint, computed once. */
const buildPath = () => {
  const segments = [];
  // Start where the first chord's tail lands, so the finished figure is centred
  // on the platform rather than drifting off one side of it.
  let x = -STEP_LENGTH / 2;
  let z = -RADIUS * Math.cos(Math.asin(STEP_LENGTH / (2 * RADIUS)));
  let heading = 0;

  for (let i = 0; i < STEPS; i += 1) {
    const nx = x + Math.cos(heading) * STEP_LENGTH;
    const nz = z + Math.sin(heading) * STEP_LENGTH;
    segments.push({
      mid: [(x + nx) / 2, (z + nz) / 2],
      angle: -heading,
      end: [nx, nz],
      heading,
    });
    x = nx;
    z = nz;
    heading += TURN;
  }
  return segments;
};

const Path = ({ steps, accent, top, reduced }) => {
  const segments = useMemo(buildPath, []);
  const meshes = useRef([]);
  // Per-segment reveal, so a chord grows out of the turtle instead of blinking
  // into existence — at 55ms per step, popping reads as flicker.
  const grown = useRef(new Array(STEPS).fill(0));
  const turtle = useRef();

  useFrame((_, delta) => {
    const step = Math.min(delta, 0.05);
    segments.forEach((_seg, i) => {
      const mesh = meshes.current[i];
      if (!mesh) return;
      const target = i < steps ? 1 : 0;
      grown.current[i] = reduced
        ? target
        : THREE.MathUtils.damp(grown.current[i], target, 22, step);
      if (Math.abs(grown.current[i] - target) < 0.01) grown.current[i] = target;
      mesh.visible = grown.current[i] > 0.01;
      mesh.scale.x = grown.current[i];
    });

    const t = turtle.current;
    if (!t) return;
    const at = steps === 0 ? null : segments[steps - 1];
    const target = at ? at.end : [segments[0].mid[0] - STEP_LENGTH / 2, segments[0].mid[1]];
    t.position.x = reduced ? target[0] : THREE.MathUtils.damp(t.position.x, target[0], 18, step);
    t.position.z = reduced ? target[1] : THREE.MathUtils.damp(t.position.z, target[1], 18, step);
    t.rotation.y = -(at ? at.heading + TURN : 0);
  });

  return (
    <group position={[0, top + LIFT, 0]}>
      {segments.map((seg, i) => (
        <mesh
          key={i}
          ref={(m) => { meshes.current[i] = m; }}
          position={[seg.mid[0], 0, seg.mid[1]]}
          rotation={[0, seg.angle, 0]}
          visible={false}
        >
          <boxGeometry args={[STEP_LENGTH, THICKNESS, THICKNESS]} />
          <meshStandardMaterial
            color={accent}
            emissive={accent}
            emissiveIntensity={0.9}
            roughness={0.5}
            toneMapped={false}
          />
        </mesh>
      ))}

      {/* The turtle: a wedge pointing the way it is about to go. */}
      <mesh ref={turtle} position={[0, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.13, 0.3, 4]} />
        <meshStandardMaterial color="#FFD97A" emissive="#FFD97A" emissiveIntensity={0.6} toneMapped={false} />
      </mesh>
    </group>
  );
};

const Framing = ({ top }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    // Steeper than the other demos: the drawing is flat on the deck, and from a
    // low angle a star polygon collapses into a smear of overlapping lines.
    const narrow = size.width < 640;
    camera.position.set(0, top + (narrow ? 6.0 : 5.4), narrow ? 4.6 : 4.2);
    camera.lookAt(0, top, 0);
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, top]);

  return null;
};

const Scene = ({ steps, accent, accentInk, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const logo = useCloned('/Python.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  return (
    <>
      <Framing top={top} />

      <ambientLight intensity={0.55} />
      <hemisphereLight args={['#9DC3E6', '#0A1020', 0.6]} />
      <directionalLight position={[4, 9, 5]} intensity={1.2} />

      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.45} position={[0, top - 0.4, 0]} />

      {/* The logo watches from the back of the deck — it is the course's mark,
          not the subject, so it stays out of the drawing's way. */}
      <group position={[0, top + 0.42, -2.15]} rotation={[-0.62, 0, 0]}>
        <Fitted object={logo} size={0.95} fit="max" align="center" />
      </group>

      <Path steps={steps} accent={accent} top={top} reduced={reduced} />

      <pointLight color={accent} intensity={1.6} distance={7} decay={2} position={[0, top + 2.2, 1]} />
    </>
  );
};

/* ── Demo ─────────────────────────────────────────────────────────────────── */

const CODE_LINES = [
  'for i in range(36):',
  '    turtle.forward(120)',
  '    turtle.right(170)',
];

const TurtleDemo = ({ accent, accentInk, reduced = false, onFirstUse, onRun }) => {
  const [steps, setSteps] = useState(0);
  const [running, setRunning] = useState(false);
  const announced = useRef(false);

  const done = steps >= STEPS;

  const announce = useCallback(() => {
    if (announced.current) return;
    announced.current = true;
    if (onFirstUse) onFirstUse();
  }, [onFirstUse]);

  const stepOnce = useCallback(() => {
    if (running || done) return;
    setSteps((n) => Math.min(STEPS, n + 1));
    announce();
  }, [running, done, announce]);

  const run = useCallback(() => {
    if (running || done) return;
    setRunning(true);
    announce();
    if (onRun) onRun();
  }, [running, done, announce, onRun]);

  // The loop advances on a timer rather than per frame, so it runs at the same
  // speed with or without a canvas — and so the count is the same thing a
  // screen reader hears either way.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setSteps((n) => {
        if (n + 1 >= STEPS) {
          setRunning(false);
          return STEPS;
        }
        return n + 1;
      });
    }, reduced ? 8 : RUN_INTERVAL);
    return () => clearInterval(id);
  }, [running, reduced]);

  const reset = useCallback(() => {
    setRunning(false);
    setSteps(0);
  }, []);

  return (
    <DemoShell
      camera={{ fov: 40, near: 0.1, far: 60, position: [0, 5.8, 4.2] }}
      scene={<Scene steps={steps} accent={accent} accentInk={accentInk} reduced={reduced} />}
      fallback={
        // No WebGL: the same count as a bar of ticks.
        <div className="demo-flat" style={{ gridTemplateColumns: 'repeat(12, 1fr)' }} aria-hidden="true">
          {new Array(STEPS).fill(0).map((_, i) => (
            <span key={i} className={i < steps ? 'tile' : 'tile gone'} />
          ))}
        </div>
      }
      status={
        <>
          <CornerDownLeft size={16} aria-hidden="true" />
          הרצנו <strong>{steps}</strong> מתוך {STEPS} סיבובי לולאה
          {done && <em> — הצורה הושלמה</em>}
        </>
      }
      code={{
        tag: 'PYTHON',
        label: 'קוד פייתון לדוגמה: לולאה שמציירת כוכב בעל 36 קודקודים',
        lines: CODE_LINES,
        activeLine: running ? 1 : null,
      }}
      controls={
        <>
          <button type="button" className="gg-btn-ghost" onClick={stepOnce} disabled={running || done}>
            <CornerDownLeft size={16} aria-hidden="true" /> צעד אחד
          </button>
          <button type="button" className="gg-btn" onClick={run} disabled={running || done}>
            <Play size={16} aria-hidden="true" /> {running ? 'רץ...' : 'הריצו את הלולאה'}
          </button>
        </>
      }
      hint={
        done
          ? 'הצורה הזאת היא 36 פקודות — שנכתבו בשלוש שורות. זה כל הרעיון של לולאה.'
          : 'לחצו צעד אחד כמה פעמים, ואז תנו ללולאה לסיים את העבודה.'
      }
      onReset={reset}
      resetDisabled={steps === 0}
      resetLabel="ניקוי הלוח"
    />
  );
};

export default TurtleDemo;
